import { useCallback, useEffect, useState } from "react";
import {
  Users,
  UserPlus,
  KeyRound,
  Trash2,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  X,
} from "lucide-react";
import { API, formatApiErrorDetail } from "../auth/api";

const MIN_LEN = 12;

function randomTempPassword() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789-_";
  const bytes = new Uint32Array(18);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

export default function UsersDialog({ open, onClose, currentUser }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ email: "", name: "", role: "member", temp_password: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/users");
      setRows(data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const createUser = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    if (form.temp_password.length < MIN_LEN) {
      setError(`Temporary password must be at least ${MIN_LEN} characters.`);
      return;
    }
    setBusyId("new");
    try {
      await API.post("/users", form);
      setNotice(
        `Created ${form.email}. Share the temporary password with them securely — they must change it on first sign-in.`
      );
      setForm({ email: "", name: "", role: "member", temp_password: "" });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const patchUser = async (id, patch) => {
    setError("");
    setNotice("");
    setBusyId(id);
    try {
      await API.patch(`/users/${id}`, patch);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const resetPassword = async (row) => {
    const temp = randomTempPassword();
    setError("");
    setNotice("");
    setBusyId(row.id);
    try {
      await API.post(`/users/${row.id}/reset-password`, { temp_password: temp });
      setNotice(`Temporary password for ${row.email}: ${temp} — share it securely, it must be changed on next sign-in.`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const removeUser = async (row) => {
    if (!window.confirm(`Remove ${row.email}? Their sessions end immediately.`)) return;
    setError("");
    setNotice("");
    setBusyId(row.id);
    try {
      await API.delete(`/users/${row.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/70 backdrop-blur-sm p-6 overflow-auto"
      data-testid="users-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Team accounts"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-3xl border border-white/10 bg-[#0a0a0c] my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5 text-cyan-300" />
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-zinc-200">
              Team Accounts
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="users-dialog-close"
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {(error || notice) && (
          <div className="px-5 pt-4 space-y-2">
            {error && (
              <div
                className="flex items-start gap-2 border border-rose-400/30 bg-rose-400/5 px-3 py-2"
                data-testid="users-error"
              >
                <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
                <span className="font-mono text-[11px] text-rose-200">{error}</span>
              </div>
            )}
            {notice && (
              <div
                className="border border-amber-400/30 bg-amber-400/5 px-3 py-2 font-mono text-[11px] text-amber-200 break-all"
                data-testid="users-notice"
              >
                {notice}
              </div>
            )}
          </div>
        )}

        <form onSubmit={createUser} className="px-5 py-4 border-b border-white/10 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-3 h-3 text-emerald-300" />
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500">
              Add a rep
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="email"
              required
              placeholder="email@virtuealliance.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="new-user-email"
              className="bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none px-3 py-2 font-mono text-[12px] text-zinc-100 placeholder:text-zinc-700"
            />
            <input
              type="text"
              required
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="new-user-name"
              className="bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none px-3 py-2 font-mono text-[12px] text-zinc-100 placeholder:text-zinc-700"
            />
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="new-user-role"
              className="bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none px-3 py-2 font-mono text-[12px] text-zinc-100"
            >
              <option value="member">Member — can view and edit records</option>
              <option value="admin">Admin — can also manage accounts</option>
            </select>
            <div className="flex gap-2">
              <input
                type="text"
                required
                placeholder="Temporary password"
                value={form.temp_password}
                onChange={(e) => setForm({ ...form, temp_password: e.target.value })}
                data-testid="new-user-temp-password"
                className="flex-1 min-w-0 bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none px-3 py-2 font-mono text-[12px] text-zinc-100 placeholder:text-zinc-700"
              />
              <button
                type="button"
                onClick={() => setForm({ ...form, temp_password: randomTempPassword() })}
                data-testid="new-user-generate"
                className="px-3 border border-white/10 hover:border-cyan-400/60 font-mono text-[10px] tracking-[0.16em] uppercase text-zinc-400 hover:text-cyan-300 transition-colors"
              >
                Generate
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={busyId === "new"}
            data-testid="create-user-submit"
            className="flex items-center gap-2 px-4 py-2 border border-emerald-400/60 bg-emerald-400/10 hover:bg-emerald-400/20 disabled:opacity-50 font-mono text-[10px] tracking-[0.22em] uppercase text-emerald-200 transition-colors"
          >
            {busyId === "new" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Create Account
          </button>
        </form>

        <div className="divide-y divide-white/5" data-testid="users-list">
          {loading ? (
            <div className="px-5 py-6 flex items-center gap-2 font-mono text-[11px] text-zinc-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading accounts…
            </div>
          ) : (
            rows.map((u) => (
              <div
                key={u.id}
                className="px-5 py-3 flex items-center gap-3 flex-wrap"
                data-testid={`user-row-${u.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-zinc-100 truncate">{u.name}</span>
                    {u.role === "admin" && (
                      <span className="flex items-center gap-1 font-mono text-[9px] tracking-[0.16em] uppercase text-cyan-300 border border-cyan-400/40 px-1.5 py-0.5">
                        <ShieldCheck className="w-2.5 h-2.5" /> Admin
                      </span>
                    )}
                    {u.disabled && (
                      <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-rose-300 border border-rose-400/40 px-1.5 py-0.5">
                        Disabled
                      </span>
                    )}
                    {u.must_change_password && (
                      <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-amber-300 border border-amber-400/40 px-1.5 py-0.5">
                        Temp password
                      </span>
                    )}
                    {u.id === currentUser?.id && (
                      <span className="font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-500">
                        You
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate">{u.email}</div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={busyId === u.id}
                    onClick={() => patchUser(u.id, { role: u.role === "admin" ? "member" : "admin" })}
                    data-testid={`toggle-role-${u.id}`}
                    className="px-2.5 py-1 border border-white/10 hover:border-cyan-400/60 disabled:opacity-40 font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-400 hover:text-cyan-300 transition-colors"
                  >
                    {u.role === "admin" ? "Make Member" : "Make Admin"}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === u.id}
                    onClick={() => patchUser(u.id, { disabled: !u.disabled })}
                    data-testid={`toggle-disabled-${u.id}`}
                    className="px-2.5 py-1 border border-white/10 hover:border-amber-400/60 disabled:opacity-40 font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-400 hover:text-amber-300 transition-colors"
                  >
                    {u.disabled ? "Enable" : "Disable"}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === u.id}
                    onClick={() => resetPassword(u)}
                    title="Issue a temporary password"
                    data-testid={`reset-password-${u.id}`}
                    className="p-1.5 border border-white/10 hover:border-cyan-400/60 disabled:opacity-40 text-zinc-400 hover:text-cyan-300 transition-colors"
                  >
                    <KeyRound className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    disabled={busyId === u.id}
                    onClick={() => removeUser(u)}
                    title="Remove account"
                    data-testid={`delete-user-${u.id}`}
                    className="p-1.5 border border-white/10 hover:border-rose-400/60 disabled:opacity-40 text-zinc-400 hover:text-rose-300 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/10 font-mono text-[9px] tracking-[0.18em] uppercase text-zinc-600">
          Members can view and edit all contacts · Only admins manage accounts and backups
        </div>
      </div>
    </div>
  );
}
